import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ConsignmentDetail, ConsignmentInboundItem, ConsignmentOutboundItem } from '@cuito/types';
import { Button } from '@cuito/ui';
import {
  CONSIGNMENT_PAGE_SIZE,
  EMPTY_CONSIGNMENT_FILTERS,
  consignmentQueryOf,
  consignmentsApi,
  type ConsignmentFilterInput,
  type ConsignmentTab,
} from '../api/consignments';
import ConsignmentTable, { consignmentRowOf, type ConsignmentRow } from '../components/consignment/ConsignmentTable';
import { readErrorCode, readServerMessage } from '../lib/apiError';
import { useAuthStore } from '../stores/authStore';

/**
 * 寄售台账页（契约 §14；UI 规范 550 行的 `/consignments`）。
 *
 * 三件必须坚持的事：
 *   ① **两个 tab 就是两个端点**（借出 = `outbound`，受托 = `inbound`）：切 tab 只发对应请求，
 *      绝不「先取一个再用另一个过滤」；
 *   ② **筛选是暂存 + 应用**：`<select>`/勾选框只改草稿，`filter-search`（提交按钮）才发请求，
 *      并且**页码回到 1**（换了筛选还停在第 3 页，用户看到的是空页）；
 *   ③ **无 `consign:read` ⇒ 一个请求都不发**（服务端仍会 403；前端隐藏只是体验）。
 *
 * 展开条目走**第二个端点**（§14.3）：行点开才取详情，同一次打开内的重复点击不重复取
 * （`expandedId` 相同 ⇒ 收起），并且用独立的请求序号守卫（详情与列表互不干扰）。
 */
const CROSS_TENANT_TEXT = '无权查看其他社团的寄售台账';

function tabClass(selected: boolean): string {
  return `rounded-lg border-2 border-black px-4 py-2 text-sm font-bold ${
    selected ? 'bg-black text-white' : 'bg-white text-black'
  }`;
}

/** 对方社团下拉的选项：从**当前页的行**里按 id 去重（待裁决 D-2：没有「我的受托方列表」端点） */
function counterpartyOptionsOf(
  rows: ConsignmentRow[],
  current: string,
): Array<{ id: string; name: string }> {
  const options = rows.map((row) => ({ id: row.counterpartyTenantId, name: row.counterpartyName }));
  // 当前选中的值即使不在本页里也要保留一项，否则 `<select>` 会因为找不到匹配的 option 而显示空白
  if (current !== '' && !options.some((option) => option.id === current)) {
    options.push({ id: current, name: current });
  }
  return options.filter(
    (option, index, all) => all.findIndex((other) => other.id === option.id) === index,
  );
}

export default function ConsignmentsPage() {
  const canRead = useAuthStore((s) => s.hasPermission('consign:read'));

  const [tab, setTab] = useState<ConsignmentTab>('outbound');
  const [draft, setDraft] = useState<ConsignmentFilterInput>(EMPTY_CONSIGNMENT_FILTERS);
  const [applied, setApplied] = useState<ConsignmentFilterInput>(EMPTY_CONSIGNMENT_FILTERS);
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<ConsignmentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConsignmentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const query = useMemo(() => consignmentQueryOf(applied, tab), [applied, tab]);

  /** 单调递增的请求序号：先发的请求后到时**丢弃**它的响应（否则页面会跳回上一次筛选的结果） */
  const listSeq = useRef(0);
  const detailSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (!canRead) return;
    listSeq.current += 1;
    const token = listSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = { ...query, page, pageSize: CONSIGNMENT_PAGE_SIZE };
      const result =
        tab === 'outbound'
          ? await consignmentsApi.outbound(params)
          : await consignmentsApi.inbound(params);
      if (token !== listSeq.current) return;
      const items = result.items as Array<ConsignmentOutboundItem | ConsignmentInboundItem>;
      setRows(items.map((item) => consignmentRowOf(item, tab)));
      setTotal(result.total);
      setTotalPages(Math.max(1, result.totalPages));
      setCurrentPage(result.page);
    } catch (e) {
      if (token !== listSeq.current) return;
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      if (token === listSeq.current) setLoading(false);
    }
  }, [canRead, page, query, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDetail = useCallback(async (id: string): Promise<void> => {
    detailSeq.current += 1;
    const token = detailSeq.current;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const row = await consignmentsApi.detail(id);
      if (token !== detailSeq.current) return;
      setDetail(row);
    } catch (e) {
      if (token !== detailSeq.current) return;
      // `CONSIGNMENT_NOT_FOUND` 用 UI 规范 §550 的逐字文案（「跨租户行不出现，不是报错」）；
      // 其它错误一律服务端 message 优先，读不到再兜底。
      setDetailError(
        readErrorCode(e) === 'CONSIGNMENT_NOT_FOUND'
          ? CROSS_TENANT_TEXT
          : (readServerMessage(e) ?? '加载失败，请重试'),
      );
    } finally {
      if (token === detailSeq.current) setDetailLoading(false);
    }
  }, []);

  const toggleRow = (id: string): void => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      setDetailError(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    void loadDetail(id);
  };

  const switchTab = (next: ConsignmentTab): void => {
    if (next === tab) return;
    setTab(next);
    setPage(1);
    setExpandedId(null);
    setDetail(null);
    setDetailError(null);
    // 「仅看已售」只在借出 tab 有效；切到受托时把它清掉，切回来时保持用户的暂存选择
    if (next === 'inbound') setDraft((current) => ({ ...current, hasSold: false }));
  };

  const applyFilters = (): void => {
    setApplied(draft);
    setPage(1);
    setExpandedId(null);
  };

  if (!canRead) {
    return (
      <div className="max-w-7xl" data-testid="consignments-page">
        <p className="font-bold text-red-600" data-testid="consignments-error">
          {CROSS_TENANT_TEXT}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl" data-testid="consignments-page">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">寄售台账</h1>

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          data-testid="consignments-tab-outbound"
          aria-pressed={tab === 'outbound'}
          onClick={() => switchTab('outbound')}
          className={tabClass(tab === 'outbound')}
        >
          借出
        </button>
        <button
          type="button"
          data-testid="consignments-tab-inbound"
          aria-pressed={tab === 'inbound'}
          onClick={() => switchTab('inbound')}
          className={tabClass(tab === 'inbound')}
        >
          受托
        </button>
      </div>

      <section className="mb-4 flex flex-wrap items-end gap-3">
        {tab === 'outbound' ? (
          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">受托方</span>
            <select
              data-testid="filter-holder"
              value={draft.counterpartyTenantId}
              onChange={(event) => setDraft((current) => ({ ...current, counterpartyTenantId: event.target.value }))}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base"
            >
              <option value="">全部受托方</option>
              {counterpartyOptionsOf(rows, draft.counterpartyTenantId).map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">货主</span>
            <select
              data-testid="filter-owner"
              value={draft.counterpartyTenantId}
              onChange={(event) => setDraft((current) => ({ ...current, counterpartyTenantId: event.target.value }))}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base"
            >
              <option value="">全部货主</option>
              {counterpartyOptionsOf(rows, draft.counterpartyTenantId).map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {tab === 'outbound' && (
          <label className="flex items-center gap-2 pb-3">
            <input
              type="checkbox"
              data-testid="filter-has-sold"
              checked={draft.hasSold}
              onChange={(event) => setDraft((current) => ({ ...current, hasSold: event.target.checked }))}
              className="h-5 w-5"
            />
            <span className="text-sm font-bold">仅看已售</span>
          </label>
        )}

        <Button data-testid="filter-search" variant="secondary" onClick={applyFilters}>
          搜索
        </Button>
      </section>

      <p className="mb-3 text-sm text-gray-600" data-testid="consignments-notice">
        第 {currentPage} / {totalPages} 页 · 共 {total} 行
      </p>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error !== null && (
        <p className="font-bold text-red-600" data-testid="consignments-error">
          {error}
        </p>
      )}
      {!loading && error === null && rows.length === 0 && <p className="text-gray-500">暂无寄售记录</p>}
      {!loading && error === null && rows.length > 0 && (
        <>
          <ConsignmentTable
            tab={tab}
            rows={rows}
            expandedId={expandedId}
            detail={detail}
            detailLoading={detailLoading}
            detailError={detailError}
            onToggle={toggleRow}
          />
          <div className="mt-4 flex items-center gap-3">
            <Button
              data-testid="consignments-prev"
              size="sm"
              variant="secondary"
              disabled={currentPage <= 1}
              onClick={() => {
                setExpandedId(null);
                setPage((current) => Math.max(1, current - 1));
              }}
            >
              上一页
            </Button>
            <Button
              data-testid="consignments-next"
              size="sm"
              variant="secondary"
              disabled={currentPage >= totalPages}
              onClick={() => {
                setExpandedId(null);
                setPage((current) => current + 1);
              }}
            >
              下一页
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
